import React, { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { Context,Hooks,Util,Request,Storage } from "@xt/xtbase";
const {XtContext,XtAuth} = Context;
const {useTranslation} = Hooks;
const {Big,getUrl,showRate} = Util;
const {XtSocketPublic} = Request;
import styles from "./index.module.less";
import { useStore } from '@/models';
import { observer } from 'mobx-react-lite';
import {get_ticker24h} from "@/api/v4/market";
import { get_convertConfigList } from "@/api/v4/convert";
import XtButton from "@/components/App/XtButton";
import XtScroll from "@/components/App/XtScroll";
import NoData from "@/components/App/NoData";
import List from "./list";
import PcListRow from "../MarketsPc/PcListRow";
import RecommendPc from "../MarketsPc/RecommendPc";
import pcStyles from "../MarketsPc/index.module.less";
import Recommend from "./recommend";
import FavMenu from "./favMenu";
import AddFavTooltip from "./addFavTooltip";
import { includeInCfdMarketTabRow } from "./cfdTabListSource";
import {
  compareSpotByPriceDesc,
  findSpotTickerRecord,
  getSpotMarketConfig,
  isSpotSymbolAvailable,
  resolveSpotDisplaySymbolsFromConvertConfig,
  spotSymbolMatchesSubTab,
} from "@/utils/spotConvertSymbols";
import { marketRowMatchesFavSubTab } from "@/utils/favSubTab";
import { getDynamicFiatMarketCodes } from "@/utils/fiatMarket";
import { parseSymbolParts, resolveMarketCap } from "../MarketsPc/utils";
import { searchMarketsFromConfig } from "@/utils/marketSearch";
import { usePcSparklines } from "../MarketsPc/usePcSparklines";

const SPOT_SUB_TABS = [
  { key: "usdt", label: "USDT" },
  { key: "usdc", label: "USDC" },
  { key: "fiat", label: "Fiat" },
];

/** PC 列表默认行数；K 线仅在点击 More 后按需拉取 */
const PC_LIST_INITIAL_ROWS = 10;

function stripSpotCfdSuffix(symbol) {
  return String(symbol || "").replace(/_cfd$/i, "");
}

function toSpotCfdSymbol(symbol) {
  const base = stripSpotCfdSuffix(symbol);
  return base ? `${base}_cfd` : "";
}

function isSpotDisplaySymbol(symbol) {
  const s = String(symbol || "");
  return s.length > 0 && !s.toLowerCase().endsWith("_cfd");
}

const MarketList = (props) => {
  const t = useTranslation();
  const {market, user, fund} = useStore();
  // 收藏列表是否已完成首次加载：未加载完前不能判断"无收藏"，避免推荐态闪烁
  const [userMarketLoaded, setUserMarketLoaded] = useState(false);
  useEffect(()=>{
    if(user.loginStatus == 1)
    {
      setUserMarketLoaded(false);
      Promise.resolve(user.getUserMarket())
        .catch(()=>{})
        .finally(()=>setUserMarketLoaded(true));
    } else {
      setUserMarketLoaded(true);
    }
  },[user.loginStatus]);
  const [appState] = React.useContext(XtContext);
  const { loginStatus, theme } = appState;

  const [tab,setTab] = useState("");
  const [internalSpotSubTab, setInternalSpotSubTab] = useState("usdt");
  const spotSubTab = props.spotSubTab !== undefined ? props.spotSubTab : internalSpotSubTab;
  const setSpotSubTab = (key) => {
    if (props.spotSubTab === undefined) setInternalSpotSubTab(key);
  };
  const tabSave = useRef({});
  // 与 Fiat-M 一级 Tab 一致：仅当存在可交易法币（plate 258 且 isSymbolExist=1）时展示 Fiat 子 Tab
  const showSpotFiatTab = useMemo(() => {
    if (props.showFiatTab != null) return !!props.showFiatTab;
    return getDynamicFiatMarketCodes().length > 0;
  }, [props.showFiatTab, fund?.recommendedCurrencyList, fund?.recommendedCurrencyListMd5]);
  const visibleSpotSubTabs = useMemo(
    () => SPOT_SUB_TABS.filter((item) => item.key !== "fiat" || showSpotFiatTab),
    [showSpotFiatTab]
  );

  const [internalInputValue, setInternalInputValue] = useState("");
  const inputRef = useRef();
  const [internalSortBy, setInternalSortBy] = useState("");
  const isPcLayout = props.layout === "pc";
  const inputValue = props.searchValue !== undefined ? props.searchValue : internalInputValue;
  const setInputValue = (value) => {
    const next = typeof value === "string" ? value : value?.target?.value ?? "";
    if (props.onSearchChange) props.onSearchChange(next);
    else setInternalInputValue(next);
  };
  const sortBy = props.sortBy !== undefined ? props.sortBy : internalSortBy;
  const setSortBy = (value) => {
    if (props.onSortChange) props.onSortChange(value);
    else setInternalSortBy(value);
  };

  const [resData, setResData] = useState([]);
  const [wsData, setWsData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [pcListExpanded, setPcListExpanded] = useState(false);
  const [tickerLoading, setTickerLoading] = useState(true);
  const [spotConfigLoading, setSpotConfigLoading] = useState(false);
  /** Spot：convert/config/list 支持的加密货币对应的展示交易对（不带 _cfd） */
  const [spotDisplaySymbols, setSpotDisplaySymbols] = useState([]);
  const spotSymbolSetRef = useRef(new Set());

  // Favourite 相关：长按菜单；顺序与接口返回的 userMarket 一致
  const [favMenu, setFavMenu] = useState(null); // { x, y, symbol } | null
  // Fiat-M / USDT-M 长按 "Add to Favorites" 气泡
  const [addFavTip, setAddFavTip] = useState(null); // { x, y, symbol } | null
  const changeTab = (t)=>{
    Storage.set("marketGroup",t);
    if(t == "user" && loginStatus != 1)
    {
      // 未登录时，user tab 不可用，但新的 tab 分类只有 user tab，所以保持原值
      // 实际使用中，user tab 会在 UI 层面根据登录状态隐藏
    }
    tabSave.current = {...tabSave.current,[props.marketType]:t};
    setTab(t);
  }
  
  // 移除 user tab，因为已经移到 parent-tabs 中
  const marketTypeCfgAry = [];
  const marketTypeCfgAryDisplay = marketTypeCfgAry;

  const onClickTab = (key)=>{
    if(tab != key)
    {
      changeTab(key);
    }
  }

  const onClickSpotSubTab = (key) => {
    if (spotSubTab !== key) {
      setSpotSubTab(key);
      setSortBy("");
    }
  };

  const onInputSearch = (e)=>{
    var s = e.target.value.trim();
    setInputValue(s);
  }
  const onClickInput = (e)=>{
    setInputValue("");
    inputRef.current?.focus();
  }
  const clickSort = (attr) => {
    let value;
    if (!sortBy) {
      value = attr + "_down";
    } else {
      const ary = sortBy.split("_");
      if (attr !== ary[0]) {
        value = attr + "_down";
      } else {
        if (ary[1] === "down") {
          value = attr + "_up";
        } else {
          value = "";
        }
      }
    }
    setSortBy(value);
  }
  useEffect(()=>{
    // 不再需要子 tab，因为 user tab 已经移到 parent-tabs 中
    // 直接设置 tab 为空，不显示子 tab
    setTab("");
    return ()=>{
      XtSocketPublic.removeChannel({ event: "tickers"});
    }
  },[]);

  useEffect(() => {
    if (props.marketType === "spot") {
      if (props.spotSubTab === undefined) setInternalSpotSubTab("usdt");
      if (props.sortBy === undefined) setSortBy("price_down");
    } else if (props.sortBy === undefined) {
      setSortBy("");
    }
  }, [props.marketType, props.spotSubTab]);

  useEffect(() => {
    if (props.marketType === "spot" && !showSpotFiatTab && spotSubTab === "fiat") {
      setSpotSubTab("usdt");
    }
  }, [props.marketType, showSpotFiatTab, spotSubTab]);
  const isSpotMarket = props.marketType === "spot";

  const handleWsCallback = (records)=>{
    if (!Array.isArray(records)) return;
    setWsData((prev) => {
      const next = [...prev];
      records.forEach((obj) => {
        if (!obj?.s) return;
        let targetSymbol = obj.s;
        if (isSpotMarket) {
          // Spot 列表展示不带 _cfd，但行情 WS 走 *_cfd
          if (!obj.s.toLowerCase().endsWith("_cfd")) return;
          targetSymbol = stripSpotCfdSuffix(obj.s);
          if (!targetSymbol || !spotSymbolSetRef.current.has(targetSymbol)) return;
        }
        const index = next.findIndex((doc) => doc.s === targetSymbol);
        const mapped = { ...obj, s: targetSymbol };
        if (index < 0) {
          mapped.oc = mapped.c;
          next.push(mapped);
        } else {
          mapped.oc = next[index].c;
          next.splice(index, 1, mapped);
        }
      });
      market.tickers = next;
      return next;
    });
  };
  useEffect(() => {
    if (props.marketType !== "spot") {
      setSpotDisplaySymbols([]);
      spotSymbolSetRef.current = new Set();
      setSpotConfigLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSpotConfigLoading(true);
    get_convertConfigList({ isAutoPopError: false })
      .then((res) => {
        if (cancelled) return;
        const raw = res?.result ?? res?.data ?? res;
        const list = Array.isArray(raw) ? raw : [];
        const { marketConfig } = market;
        const symbols =
          marketConfig && Object.keys(marketConfig).length > 0
            ? resolveSpotDisplaySymbolsFromConvertConfig(list, marketConfig, {
                activeFiatCodes: props.activeFiatCodes,
                activeFiatCode: props.activeFiatCode,
              })
            : [];
        setSpotDisplaySymbols(symbols);
        spotSymbolSetRef.current = new Set(symbols);
      })
      .catch(() => {
        if (!cancelled) {
          setSpotDisplaySymbols([]);
          spotSymbolSetRef.current = new Set();
        }
      })
      .finally(() => {
        if (!cancelled) setSpotConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.marketType, market.marketConfig, props.activeFiatCodes, props.activeFiatCode]);

  useLayoutEffect(() => {
    if (props.layout === "pc") {
      const isCfd = props.marketType === "fiat-m" || props.marketType === "usdt-m";
      if (isCfd) setTickerLoading(true);
    }
  }, [props.layout, props.marketType]);

  useEffect(()=>{
    const {marketConfig} = market;
    const isSpot = props.marketType === "spot";
    const isOptional = props.marketType === "optional";
    const isCfd = props.marketType === "fiat-m" || props.marketType === "usdt-m";
    const needsListLoading = isSpot || isOptional || (props.layout === "pc" && isCfd);

    console.log("[MarketList] useEffect - marketType:", props.marketType, "marketConfig keys:", marketConfig ? Object.keys(marketConfig).length : 0);
    if (!marketConfig || Object.keys(marketConfig).length === 0) {
      console.warn("[MarketList] marketConfig 为空或未加载");
      if (needsListLoading || props.layout === "pc") setTickerLoading(true);
      return;
    }

    if (isSpot && spotConfigLoading) {
      setTickerLoading(true);
      return;
    }

    if (isSpot && !spotDisplaySymbols.length) {
      setResData([]);
      setWsData([]);
      setTickerLoading(false);
      return;
    }

    if (isSpot) {
      setWsData([]);
    }

    if (needsListLoading) setTickerLoading(true);

      let retData = [];
      get_ticker24h()
        .then((data) => {
          retData = data;
        console.log("[MarketList] get_ticker24h 返回数据数量:", retData ? retData.length : 0, "前3个:", retData ? retData.slice(0, 3) : []);
        })
        .finally(() => {
          const ary = [];
          let doc;
          if (props.marketType === "spot") {
            spotDisplaySymbols.forEach((va) => {
              doc = findSpotTickerRecord(va, retData);
              ary.push(doc);
            });
          } else {
            for (let va in marketConfig) {
              doc = retData.find((obj) => obj.s === va) || { s: va };
              ary.push(doc);
            }
          }
        console.log("[MarketList] resData 设置前，ary 数量:", ary.length, "前3个:", ary.slice(0, 3));
          setResData(ary);
          if (needsListLoading) setTickerLoading(false);
        });
    
    // 不再需要子 tab，因为 user tab 已经移到 parent-tabs 中
    setTab("");
    XtSocketPublic.addChannel({ event: "tickers"}, handleWsCallback);
  },[market.marketConfig,props.marketType,spotDisplaySymbols,spotConfigLoading]);
  useEffect(() => {
    filterResData();
  }, [user.userMarket, resData, tab, inputValue, sortBy, wsData, props.subTab, props.marketType, spotDisplaySymbols, spotSubTab, props.marketCapMap, props.searchOverridesTab]);

  useEffect(() => {
    if (!isPcLayout) return;
    setPcListExpanded(false);
  }, [
    isPcLayout,
    props.marketType,
    props.subTab,
    spotSubTab,
    inputValue,
    sortBy,
    props.searchOverridesTab,
  ]);
  const doSortArray = (items)=>{
    const { marketConfig } = market;
    if (!marketConfig) return;

    items.sort((x, y) => {
      if (sortBy) {
        const ary = sortBy.split("_");
        if (ary[0] === "name") {
          if (ary[1] === "up") return strCompare(y.s, x.s);
          if (ary[1] === "down") return strCompare(x.s, y.s);
        }
        if (ary[0] === "price") {
          if (ary[1] === "up") return (x.c || 0) - (y.c || 0);
          if (ary[1] === "down") return (y.c || 0) - (x.c || 0);
        }
        if (ary[0] === "rate") {
          if (ary[1] === "up") return (x.cr || x.r || 0) - (y.cr ||  y.r || 0);
          if (ary[1] === "down") return (y.cr ||  y.r || 0) - (x.cr ||  x.r || 0);
        }

        if (ary[0] === "volume") {
          if (ary[1] === "up") return (x.v || 0) - (y.v || 0);
          if (ary[1] === "down") return (y.v || 0) - (x.v || 0);
        }

        if (ary[0] === "cap") {
          const capOf = (row) => {
            const { base } = parseSymbolParts(row.s);
            return resolveMarketCap(row, fund, base, props.marketCapMap) || 0;
          };
          if (ary[1] === "up") return capOf(x) - capOf(y);
          if (ary[1] === "down") return capOf(y) - capOf(x);
        }
      }
      // 不再需要检查 tab === "user"，因为 user tab 已经移到 parent-tabs 中
      // optional tab：顺序与接口自选列表 userMarket 一致
      if (props.marketType === "optional") {
        const favList = Array.isArray(user.userMarket) ? user.userMarket : [];
        const xFav = favList.indexOf(x.s);
        const yFav = favList.indexOf(y.s);
        if (xFav !== -1 && yFav !== -1) return xFav - yFav;
        if (xFav === -1 && yFav === -1) return 0;
        return xFav === -1 ? 1 : -1;
      }

      if (props.marketType === "spot") {
        return compareSpotByPriceDesc(x, y);
      }

      const getDisplayWeight = (symbol) => {
        const cfg = getSpotMarketConfig(symbol, marketConfig);
        return cfg ? parseInt(cfg.displayWeight, 10) || 0 : 0;
      };
      let s1 = getDisplayWeight(x.s);
      let s2 = getDisplayWeight(y.s);
      return s2 - s1;
    });

    return items;

    function strCompare(a, b) {
      a = a.toLowerCase();
      b = b.toLowerCase();
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    }
  }
  const refreshRecords = (items)=>{
    if (!items) return;
    if (items instanceof Array) return doSortArray(items);
    for (let va in items) doSortArray(items[va]);
  }
  const filterGroupMarketRecord = (records) => {
    const { marketConfig } = market;

    if (!marketConfig) {
      console.error("【logErr】市场配置不存在 marketConfig");
      return [];
    }

    const data = [];
    let passedCount = 0;
    let filteredCount = 0;

    records.map((item) => {
      const symbol = item.s;
      const theMarket =
        props.marketType === "spot"
          ? getSpotMarketConfig(symbol, marketConfig)
          : marketConfig[symbol]; //找到市场的对应配置
      
      // 1. Optional tab: 只显示收藏的币对
      if (props.marketType === "optional") {
        // optional tab 必须检查收藏列表
        if (!user.userMarket || !user.userMarket.length) {
          filteredCount++;
          return; // 如果没有收藏，直接过滤
        }
        if (!user.userMarket.find((s) => s === symbol)) {
          filteredCount++;
          return; // 不在收藏列表中，直接过滤
        }
        // 如果有 marketConfig，只过滤 DELISTED 状态
        if (theMarket && theMarket.state === "DELISTED") {
          filteredCount++;
          return;
        }
        // 首页 Favorites 二级 Tab：与 Fiat-M / USDT-M 相同，按 plates 260/261 分到 Crypto / Stocks / Commodities
        if (props.subTab && !marketRowMatchesFavSubTab(theMarket, props.subTab)) {
          filteredCount++;
          return;
        }
        // 如果没有 marketConfig，也显示（可能是新币对，但必须在收藏列表中）；有 subTab 时无上方的 plates 归类则归入 Crypto（无 260/261）
        passedCount++;
      } else if (props.marketType === "spot") {
        if (!isSpotDisplaySymbol(symbol) || !spotSymbolSetRef.current.has(symbol)) {
          filteredCount++;
          return;
        }
        if (!isSpotSymbolAvailable(symbol, marketConfig)) {
          filteredCount++;
          return;
        }
        if (!spotSymbolMatchesSubTab(symbol, spotSubTab)) {
          filteredCount++;
          return;
        }
        passedCount++;
      } else if (props.marketType === "fiat-m" || props.marketType === "usdt-m") {
        // Fiat-M / USDT-M：与交易页 Market 筛选一致（见 cfdTabListSource.includeInCfdMarketTabRow）
        if (
          !includeInCfdMarketTabRow(
            item,
            marketConfig,
            props.marketType,
            props.subTab,
            props.activeFiatCodes,
            props.activeFiatCode
          )
        ) {
          filteredCount++;
          return;
        }
        passedCount++;
      }
      
      // 4. 处理数据推送：Spot 展示不带 _cfd，行情优先用 *_cfd（WS 已映射为展示 symbol）
      const wsItem = wsData.find((d) => d.s == symbol);
      if (wsItem) {
        data.push({ ...wsItem, s: symbol });
      } else if (props.marketType === "spot") {
        const cfdItem = records.find((r) => r.s === toSpotCfdSymbol(symbol));
        data.push(cfdItem ? { ...cfdItem, s: symbol } : item);
      } else {
        data.push(item);
      }
    });

    console.log(`[MarketList] filterGroupMarketRecord - marketType: ${props.marketType}, tab: ${tab}, 通过: ${passedCount}, 过滤: ${filteredCount}, 总计: ${records.length}, userMarket: ${user.userMarket?.length || 0}`);
    return data;
  }
  const filterKeyword = (records, keyword) => {
    let str = keyword.replace(/\//g, "_");
    let str2 = str.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
    return records.filter((item) => {
        const marketName = item.s;
        return new RegExp(str2, "i").test(marketName);
    });
  }
  const filterResData = ()=>{
    var data = resData;
    const keyword = String(inputValue || "").trim();
    const searchingGlobally = isPcLayout && props.searchOverridesTab && keyword;

    if (searchingGlobally) {
      data = searchMarketsFromConfig({
        marketConfig: market.marketConfig,
        tickerRecords: resData,
        keyword,
        fund,
        dedupeByBase: false,
        cfdOnly: true,
      });
    } else {
      data = filterGroupMarketRecord(data);
      if (keyword) {
        data = filterKeyword(data, keyword);
      }
    }
    refreshRecords(data);
    setFilterData(data);
  }
  // Favourite 推荐态：全局无收藏；或（首页）当前 Crypto/Stocks/Commodities 子类下无任何收藏。无搜索词时才展示推荐，避免与「搜不到」混淆。
  const isOptional = props.marketType === "optional";
  /** Favourite（optional）：/market 与首页嵌入同款表头 / 行样式 */
  const showOptionalFavUi = isOptional;
  const { marketConfig } = market;
  const hasFavInCurrentSubTab =
    isOptional &&
    !!props.subTab &&
    userMarketLoaded &&
    Array.isArray(user.userMarket) &&
    user.userMarket.length > 0 &&
    marketConfig &&
    user.userMarket.some((sym) => {
      const m = marketConfig[sym];
      if (m && m.state === "DELISTED") return false;
      if (!m) return props.subTab === "crypto";
      return marketRowMatchesFavSubTab(m, props.subTab);
    });
  const showFavRecommend =
    isOptional &&
    userMarketLoaded &&
    Array.isArray(resData) &&
    resData.length > 0 &&
    !String(inputValue || "").trim() &&
    !props.searchOverridesTab &&
    ((!user.userMarket || user.userMarket.length === 0) ||
      (!!props.subTab && !hasFavInCurrentSubTab));

  const isSpotTab = props.marketType === "spot";
  const isCfdTab = props.marketType === "fiat-m" || props.marketType === "usdt-m";
  const showAiColumn = isCfdTab || isOptional;
  const hideFavSearch = (isOptional || props.marketType === "spot") && props.hideSearchBar;
  const useCompactList = isOptional || (props.marketType === "spot" && props.hideSearchBar);
  const marketConfigReady = marketConfig && Object.keys(marketConfig).length > 0;
  const isSpotOrOptional = isSpotTab || isOptional;
  const isListLoading =
    (isSpotOrOptional &&
      ((isOptional && !userMarketLoaded) ||
        !marketConfigReady ||
        (isSpotTab && spotConfigLoading) ||
        (isSpotTab && spotDisplaySymbols.length > 0 && tickerLoading) ||
        (isOptional && tickerLoading))) ||
    (isPcLayout && isCfdTab && (!marketConfigReady || tickerLoading));
  const showListLoading = isListLoading && !showFavRecommend;

  // 限制坐标不超出视窗
  const clampToViewport = (x, y, w, h) => {
    const vw = typeof window !== "undefined" ? window.innerWidth : w;
    const vh = typeof window !== "undefined" ? window.innerHeight : h;
    return {
      x: Math.max(8, Math.min(x, vw - w - 8)),
      y: Math.max(8, Math.min(y, vh - h - 8)),
    };
  };

  const handleRowLongPress = (info) => {
    if (isOptional) {
      if (showFavRecommend) return;
      // Favourite 长按菜单
      const MENU_W = 270;
      const MENU_H = 53;
      const { x, y } = clampToViewport(info.x - MENU_W / 2, info.y + 8, MENU_W, MENU_H);
      setFavMenu({ x, y, symbol: info.symbol });
      return;
    }
    if (isCfdTab) {
      // Fiat-M / USDT-M 长按显示 "Add to Favorites" 气泡
      const TIP_W = 110;
      const TIP_H = 37;
      const { x, y } = clampToViewport(info.x - TIP_W / 2, info.y - TIP_H - 8, TIP_W, TIP_H);
      setAddFavTip({ x, y, symbol: info.symbol });
      return;
    }
  };

  const closeFavMenu = () => setFavMenu(null);
  const closeAddFavTip = () => setAddFavTip(null);

  const onFavTop = () => {
    if (!favMenu) return;
    void user.pinUserMarket(favMenu.symbol);
  };

  const onFavRemove = () => {
    if (!favMenu) return;
    void user.removeUserMarket(favMenu.symbol);
  };

  const optionalFavEditSlug =
    props.marketType === "optional" &&
    (props.subTab === "crypto" || props.subTab === "stocks" || props.subTab === "commodities")
      ? props.subTab
      : "crypto";
  // 扁平 path：与历史 /market/edit-favorites 同级，避免 /market/edit-favorites/xxx 在网关/静态规则下 404
  const optionalFavEditPath =
    optionalFavEditSlug === "crypto"
      ? "/market/edit-favorites"
      : `/market/edit-favorites-${optionalFavEditSlug}`;

  const onFavEdit = () => {
    if (typeof window !== "undefined") {
      const base =
        typeof getUrl === "function" ? getUrl(optionalFavEditPath) : optionalFavEditPath;
      window.location.href = base;
    }
  };

  const showPerpBadge =
    props.marketType === "usdt-m" || props.marketType === "fiat-m";

  const visiblePcRows = useMemo(() => {
    if (!isPcLayout) return filterData;
    if (filterData.length <= PC_LIST_INITIAL_ROWS || pcListExpanded) {
      return filterData;
    }
    return filterData.slice(0, PC_LIST_INITIAL_ROWS);
  }, [isPcLayout, pcListExpanded, filterData]);

  const sparklineSymbols = useMemo(() => {
    if (!isPcLayout || showFavRecommend) return [];
    return visiblePcRows.map((item) => item.s);
  }, [isPcLayout, showFavRecommend, visiblePcRows]);

  const { getChartData } = usePcSparklines(sparklineSymbols, props.marketType, isPcLayout);

  const showPcLoadMore =
    isPcLayout &&
    !showFavRecommend &&
    !showListLoading &&
    filterData.length > PC_LIST_INITIAL_ROWS &&
    !pcListExpanded;

  const handlePcLoadMore = () => {
    setPcListExpanded(true);
  };

  const chartThemeClass = (doc) => {
    const cr = Number(doc?.cr ?? doc?.r ?? 0);
    const ret = !Number.isNaN(cr) && cr !== 0 ? (cr > 0 ? "up-color" : "down-color") : "down-color";
    if (appState?.colorReverse === "1") {
      return ret === "up-color" ? "down-color" : "up-color";
    }
    return ret;
  };

  if (isPcLayout) {
    return (
      <>
        {showListLoading ? (
          <div className={pcStyles.loading}>{t("common.loading")}</div>
        ) : showFavRecommend ? (
          <RecommendPc
            resData={resData}
            wsData={wsData}
            activeFiatCodes={props.activeFiatCodes}
            activeFiatCode={props.activeFiatCode}
            subTab={props.subTab || ""}
          />
        ) : filterData.length ? (
          <>
            {visiblePcRows.map((item, index) => (
              <PcListRow
                key={"market-pc-" + props.marketType + "-" + item.s + "-" + index}
                marketType={props.marketType}
                doc={item}
                showPerpBadge={showPerpBadge}
                marketCapMap={props.marketCapMap}
                chartData={getChartData(item.s)}
                chartTheme={chartThemeClass(item)}
              />
            ))}
            {showPcLoadMore ? (
              <div className={pcStyles.loadMoreWrap}>
                <button type="button" className={pcStyles.loadMoreBtn} onClick={handlePcLoadMore}>
                  {t("market.pcShowMore")}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <div className={pcStyles.empty}>{t("market.pcNoData") || "No Data"}</div>
        )}
        {favMenu && (
          <FavMenu
            position={favMenu}
            onClose={closeFavMenu}
            onTop={onFavTop}
            onRemove={onFavRemove}
            onEdit={onFavEdit}
          />
        )}
        {addFavTip && (
          <AddFavTooltip
            position={addFavTip}
            alreadyFav={Array.isArray(user.userMarket) && user.userMarket.includes(addFavTip.symbol)}
            onClose={closeAddFavTip}
            onConfirm={(alreadyFav) => {
              if (alreadyFav) void user.removeUserMarket(addFavTip.symbol);
              else void user.addUserMarket(addFavTip.symbol);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div>
      <div className={styles.iSticky}>
        {isSpotTab && !isPcLayout && props.spotSubTab === undefined && (
          <div className={styles.spotSubTabs}>
            {visibleSpotSubTabs.map((item) => (
              <div
                key={item.key}
                role="tab"
                tabIndex={0}
                aria-selected={spotSubTab === item.key}
                className={`${styles.spotSubTab} ${
                  spotSubTab === item.key ? styles["spotSubTab--active"] : ""
                }`}
                onClick={() => onClickSpotSubTab(item.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onClickSpotSubTab(item.key);
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
        {/* Favourite 推荐态不展示搜索框 */}
        {!showFavRecommend && !hideFavSearch && (
          <div className={styles.iHead}>
            {/* 移除子 tab，因为 user tab 已经移到 parent-tabs 中 */}
            <div>
              <div className={styles["trade-market__searchInput"] + (theme === "dark" ? " " + styles["Dark"] : "")}>
                <input ref={inputRef} value={inputValue} placeholder={t('trade.search')}
                  onInput={(e) => { onInputSearch(e) }} />
                <XtButton onClick={() => { onClickInput() }}>
                  <i className={`iconfont ${inputValue ? 'iconxinbanbendanchuangguanbi' : 'iconhome_search_gray_ico'}`} />
                </XtButton>
              </div>
            </div>
          </div>
        )}
        {/* Favourite 推荐态不展示 Name/Volume、Last Price、Change 筛选栏 */}
        {!showFavRecommend && (
          <div className={`${styles["trade-market__sort"]}${showAiColumn ? " " + styles["trade-market__sortWithAi"] : ""}`}>
            <div>
              <XtButton className={sortBy == 'name_up' ? styles.up : sortBy == 'name_down' ? styles.down : ''} onClick={() => { clickSort("name") }}>
                {t("home.Name")} / {t("trade.volume")}
              </XtButton>
            </div>
            {showAiColumn ? <div className={styles.sortHeaderAi} aria-hidden="true" /> : null}
            <div>
              <XtButton className={sortBy == 'price_up' ? styles.up : sortBy == 'price_down' ? styles.down : ''} onClick={() => { clickSort("price") }}>{t("trade.lastPrice")} </XtButton>
            </div>
            <div className={showOptionalFavUi ? styles["sortChangeCellWithIcon"] : undefined}>
              <XtButton className={sortBy == 'rate_up' ? styles.up : sortBy == 'rate_down' ? styles.down : ''} onClick={() => { clickSort("rate") }}>{t("trade.change")} </XtButton>
              {showOptionalFavUi ? (
                <a
                  href={getUrl(optionalFavEditPath)}
                  className={styles["editFavQuickLink"]}
                  aria-label={t(
                    optionalFavEditSlug === "stocks"
                      ? "market.favEditStocks"
                      : optionalFavEditSlug === "commodities"
                        ? "market.favEditCommodities"
                        : "market.favEditCrypto"
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      fill="#666666"
                      d="m10.681 7.901-3.419 3.42H10v2H5.262l-.003.002H2.666V10.73l5.422-5.42zm2.652 5.42h-2v-2h2zm-.53-7.54-1.18 1.178-2.592-2.593 1.178-1.178z"
                    />
                  </svg>
                </a>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {showListLoading ? (
        <div
          className={`${styles.listLoading}${
            props.hideSearchBar ? ` ${styles.listLoadingEmbed}` : ""
          }`}
        >
          {t("common.loading")}
        </div>
      ) : showFavRecommend ? (
        <Recommend
          resData={resData}
          wsData={wsData}
          preferFiat={!!props.showFiatTab}
          activeFiatCodes={props.activeFiatCodes}
          activeFiatCode={props.activeFiatCode}
          subTab={props.subTab || ""}
          onGoToCryptoTab={props.onGoToCryptoTab}
        />
      ) : filterData.length ? (
        useCompactList ? (
          <div className={`${styles.iBody} ${styles.iBodyCompact}`}>
            {filterData.map((item, index) => (
              <List
                marketType={props.marketType}
                key={"market-" + props.marketType + "-" + item.s + "-" + index}
                doc={item}
                onLongPress={isOptional || isCfdTab ? handleRowLongPress : undefined}
              />
            ))}
          </div>
        ) : (
          <XtScroll className={styles.iBody} height={56} dataAry={filterData} minHeight="calc(100vh - 280px)">
            {filterData.map((item,index)=>{
              return <List marketType={props.marketType} key={"market-"+props.marketType+"-"+item.s+"-"+index} 
              style={{top:56*index+"px"}}
              doc={item}
              onLongPress={isOptional || isCfdTab ? handleRowLongPress : undefined}
              >  
              </List>
            })}
          </XtScroll>
        )
      ) : (
        <NoData height={270} />
      )}
      {favMenu && (
        <FavMenu
          position={favMenu}
          onClose={closeFavMenu}
          onTop={onFavTop}
          onRemove={onFavRemove}
          onEdit={onFavEdit}
        />
      )}
      {addFavTip && (
        <AddFavTooltip
          position={addFavTip}
          alreadyFav={Array.isArray(user.userMarket) && user.userMarket.includes(addFavTip.symbol)}
          onClose={closeAddFavTip}
          // alreadyFav=true 时执行移除，否则添加
          onConfirm={(alreadyFav) => {
            if (alreadyFav) void user.removeUserMarket(addFavTip.symbol);
            else void user.addUserMarket(addFavTip.symbol);
          }}
        />
      )}
    </div>
  );
}
export default observer(MarketList);
